import React, { useEffect, useState } from "react";
import {
  NavLink,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Icon from "../../common/Icon";
import useAuth from "../../../hooks/useAuth";

const NAV_ITEMS = [
  { label: "خانه", to: "/" },
  { label: "باشگاه‌ها", to: "/clubs" },
  { label: "بازیکنان", to: "/players" },
  { label: "مسابقات", to: "/competitions" },
  { label: "تورنمنت‌ ها", to: "/tournaments" },
  { label: "اخبار", to: "/news" },
  { label: "درباره ما", to: "/about" },
  { label: "ارتباط با ما", to: "/contact" },
];

function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const isActive = (to) => {
    if (to === "/") return location.pathname === "/";
    return (
      location.pathname === to ||
      location.pathname.startsWith(`${to}/`)
    );
  };

  const getDashboardLink = () => {
    if (!user) return "/login";
    return user.role === "ADMIN"
      ? "/admin/dashboard"
      : "/club/dashboard";
  };

  const getDashboardLabel = () => {
    if (!user) return "ورود";

    return user.role === "ADMIN"
      ? "داشبورد مدیریت"
      : "داشبورد باشگاه";
  };

  const getInitials = () => {
    if (!user) return "?";

    const value =
      user.fullName ||
      user.name ||
      user.email ||
      "کاربر";

    const parts = value.trim().split(/\s+/);

    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }

    return parts[0]?.[0]?.toUpperCase() || "؟";
  };

  const handleLogout = async () => {
    await logout();
    setUserMenuOpen(false);
    navigate("/");
  };

  return (
    <>
      <header
        className={[
          "fixed inset-x-0 top-0 z-[80] px-3 transition-all duration-500 sm:px-5",
          scrolled ? "pt-2" : "pt-3 sm:pt-4",
        ].join(" ")}
      >
        <div
          className={[
            "mx-auto max-w-[1380px] rounded-full border transition-all duration-500",
            scrolled
              ? "border-white/90 bg-white/85 shadow-[0_18px_50px_-18px_rgba(10,26,19,0.35)] backdrop-blur-2xl"
              : "border-white/70 bg-white/70 shadow-[0_10px_40px_-20px_rgba(10,26,19,0.25)] backdrop-blur-xl",
          ].join(" ")}
        >
          <div className="mx-auto flex h-[64px] items-center justify-between gap-4 px-3 sm:px-4 lg:px-5">

            {/* Logo */}
            <Link
              to="/"
              className="group flex shrink-0 items-center gap-3"
            >
              <div className="relative grid h-11 w-11 place-items-center overflow-hidden rounded-full bg-ink text-floodlight shadow-lg shadow-ink/25 transition duration-500 group-hover:rotate-[360deg]">
                <div className="absolute inset-0 bg-white/10 opacity-0 transition group-hover:opacity-100" />

                <Icon
                  name="ball"
                  className="relative h-6 w-6"
                />
              </div>

              <div className="hidden sm:block">
                <div className="text-[15px] font-black leading-none text-ink">
                  سامانه فوتبال
                </div>

                <div className="mt-1 text-[10px] font-bold text-emerald-600">
                  مدیریت هیئت فوتبال شهرستان پاوه
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav
              className="hidden items-center gap-0.5 rounded-full bg-slate-100/80 p-1 lg:flex"
              aria-label="منوی اصلی"
            >
              {NAV_ITEMS.map((item) => {
                const active = isActive(item.to);

                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={[
                      "relative rounded-full px-3.5 py-2 text-[13px] font-bold transition-all duration-300",
                      active
                        ? "bg-ink text-white shadow-lg shadow-ink/25"
                        : "text-slate-600 hover:bg-white hover:text-ink",
                    ].join(" ")}
                  >
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>

            {/* Desktop Actions */}
            <div className="hidden items-center gap-2 lg:flex">

              {user ? (
                <>
                  <Link
                    to={getDashboardLink()}
                    className="inline-flex h-10 items-center gap-2 rounded-full bg-floodlight px-4 text-xs font-black text-ink shadow-lime transition hover:-translate-y-0.5 hover:bg-floodlight-300"
                  >
                    <Icon
                      name="dashboard"
                      className="h-4 w-4"
                    />
                    {getDashboardLabel()}
                  </Link>

                  <div className="relative">
                    <button
                      type="button"
                      onClick={() =>
                        setUserMenuOpen((value) => !value)
                      }
                      className="grid h-10 w-10 place-items-center rounded-full bg-gradient-to-br from-emerald-400 to-emerald-700 text-xs font-black text-white shadow-lg shadow-emerald-500/30 ring-2 ring-white transition hover:scale-105"
                    >
                      {getInitials()}
                    </button>

                    {userMenuOpen && (
                      <>
                        <button
                          type="button"
                          aria-label="بستن منوی کاربر"
                          onClick={() => setUserMenuOpen(false)}
                          className="fixed inset-0 z-40 cursor-default"
                        />

                        <div className="no-glass absolute left-0 top-14 z-50 w-64 overflow-hidden rounded-3xl border border-white bg-white shadow-2xl shadow-ink/20">
                          <div className="bg-slate-50 px-4 py-4">
                            <p className="font-black text-slate-800">
                              {user.fullName ||
                                user.name ||
                                "کاربر"}
                            </p>

                            <p className="mt-1 truncate text-xs text-slate-500">
                              {user.email}
                            </p>

                            <span className="mt-2 inline-flex rounded-full bg-emerald-100 px-2 py-1 text-[10px] font-black text-emerald-700">
                              {user.role === "ADMIN"
                                ? "مدیر سیستم"
                                : "مدیر باشگاه"}
                            </span>
                          </div>

                          <Link
                            to={getDashboardLink()}
                            className="flex items-center gap-2 px-4 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50"
                          >
                            <Icon
                              name="dashboard"
                              className="h-4 w-4"
                            />
                            داشبورد
                          </Link>

                          <button
                            type="button"
                            onClick={handleLogout}
                            className="flex w-full items-center gap-2 border-t border-slate-100 px-4 py-3 text-sm font-bold text-red-500 hover:bg-red-50"
                          >
                            <Icon
                              name="logOut"
                              className="h-4 w-4"
                            />
                            خروج از حساب
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </>
              ) : (
                <Link
                    to="/login"
                    className="inline-flex h-10 items-center gap-2 rounded-full bg-ink px-5 text-xs font-black text-white shadow-lg shadow-ink/25 transition hover:-translate-y-0.5 hover:bg-ink-800"
                  >
                    <Icon
                      name="login"
                      className="h-4 w-4"
                    />
                    ورود
                  </Link>
              )}
            </div>

            {/* Mobile */}
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label="باز کردن منو"
              className="grid h-11 w-11 place-items-center rounded-full bg-ink text-white shadow-lg shadow-ink/25 transition hover:scale-105 lg:hidden"
            >
              <Icon
                name="menu"
                className="h-5 w-5"
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[120] lg:hidden">

          <button
            type="button"
            aria-label="بستن منو"
            onClick={() => setMobileOpen(false)}
            className="absolute inset-0 bg-ink/60 backdrop-blur-md"
          />

          <aside className="no-glass absolute right-0 top-0 flex h-full w-[min(90vw,390px)] flex-col overflow-hidden rounded-l-[2.5rem] bg-pearl shadow-2xl">

            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-5">

              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3"
              >
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-700 text-white shadow-lg">
                  <Icon
                    name="ball"
                    className="h-5 w-5"
                  />
                </div>

                <div>
                  <div className="text-sm font-black text-[#0B1F17]">
                    سامانه فوتبال
                  </div>

                  <div className="mt-1 text-[10px] font-bold text-emerald-600">
                    مدیریت هیئت فوتبال شهرستان پاوه
                  </div>
                </div>
              </Link>

              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="grid h-10 w-10 place-items-center rounded-xl bg-slate-100 text-slate-600 transition hover:bg-red-50 hover:text-red-500"
              >
                <Icon
                  name="close"
                  className="h-5 w-5"
                />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-4 py-5">

              <p className="mb-3 px-2 text-[10px] font-black text-slate-400">
                دسترسی سریع
              </p>

              <div className="space-y-1.5">
                {NAV_ITEMS.map((item) => {
                  const active = isActive(item.to);

                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setMobileOpen(false)}
                      className={[
                        "flex items-center justify-between rounded-full px-5 py-4 text-sm font-black transition",
                        active
                          ? "bg-ink text-white shadow-lg shadow-ink/20"
                          : "bg-white/70 text-slate-600 hover:bg-white",
                      ].join(" ")}
                    >
                      {item.label}

                      <Icon
                        name="chevron-left"
                        className={[
                          "h-4 w-4",
                          active
                            ? "text-floodlight"
                            : "text-slate-300",
                        ].join(" ")}
                      />
                    </NavLink>
                  );
                })}
              </div>
            </nav>

            <div className="border-t border-slate-100 bg-slate-50 p-4">

              {user ? (
                <>
                  <Link
                    to={getDashboardLink()}
                    onClick={() => setMobileOpen(false)}
                    className="flex h-12 items-center justify-center gap-2 rounded-full bg-floodlight text-sm font-black text-ink shadow-lime"
                  >
                    <Icon
                      name="dashboard"
                      className="h-4 w-4"
                    />
                    {getDashboardLabel()}
                  </Link>

                  <button
                    type="button"
                    onClick={handleLogout}
                    className="mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-red-50 text-sm font-black text-red-500"
                  >
                    <Icon
                      name="logOut"
                      className="h-4 w-4"
                    />
                    خروج از حساب
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-sm font-black text-slate-700"
                >
                  ورود
                </Link>
              )}

              <p className="mt-3 text-center text-[10px] text-slate-400">
                سامانه مدیریت و توسعه هیئت فوتبال شهرستان پاوه
              </p>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}

export default Header;